import { useDateFormatter, useRouteProtection } from '@/hooks';
import { useOrganizations } from '@/pages/Organizations/hooks/useOrganizations';
import StudyContactCell, { type StudyContactCellProps } from '@/pages/Studies/components/StudyContactCell';
import StudyNameCell from '@/pages/Studies/components/StudyNameCell';
import globalStyles from '@/styles/index.module.css';
import type { UserStudyPublic } from '@/types';
import { SUPER_ADMIN_ROLE } from '@/types';
import { FullLoading } from '@components/Loaders';
import { Badge } from '@nautilusbio/nb-design-system/Badge';
import { Stack } from '@nautilusbio/nb-design-system/Stack';
import { Table } from '@nautilusbio/nb-design-system/Table';
import { Text } from '@nautilusbio/nb-design-system/Text';
import { getRouteApi, Link } from '@tanstack/react-router';
import type { ColDef, ValueFormatterParams, ValueGetterParams } from 'ag-grid-community';
import clsx from 'clsx';
import React, { type FC, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { UserActionRow } from './UserActionRow';
import styles from './UserDetails.module.css';
import { UserHeaderCard } from './UserHeaderCard';
import { inferPrimaryOrgId, useUserDetail, useUserStudies } from './useUserDetail';

type StudyContactValue = StudyContactCellProps['value'];

// getRouteApi avoids a circular import (UserDetails/index → $userId.lazy → UserDetails/index)
const routeApi = getRouteApi('/users/$userId');

// Module-level so it keeps one identity: useRouteProtection has requiredRoles in its effect
// deps, and a fresh array per render would re-run that guard effect every render.
const allowedRoles = [SUPER_ADMIN_ROLE];

const UserDetails: FC = () => {
  // Second layer alongside the route's beforeLoad (docs/ROUTE_PROTECTION.md): beforeLoad only
  // runs on navigation, so without this a role or org switch made while sitting on this page
  // leaves the whole user list rendered.
  useRouteProtection(allowedRoles);

  const { userId } = routeApi.useParams();
  const { t: tUsers } = useTranslation('users');

  const { data: user, isPending, isError: isUserError } = useUserDetail(userId);
  const { data: orgs = [] } = useOrganizations();
  const { data: studies = [], isPending: isStudiesPending, isError: isStudiesError } = useUserStudies(userId);
  const formatDate = useDateFormatter();

  const studyColDefs = useMemo<ColDef<UserStudyPublic>[]>(
    () => [
      {
        field: 'name',
        headerName: tUsers('study_col_name'),
        cellRenderer: StudyNameCell,
      },
      { field: 'org_name', headerName: tUsers('study_col_organization') },
      {
        colId: 'contact',
        headerName: tUsers('study_col_contact'),
        valueGetter: (params: ValueGetterParams<UserStudyPublic>): StudyContactValue => {
          const contact = params.data?.contact;
          if (!contact) return null;
          const name = `${contact.first_name} ${contact.last_name}`.trim();
          return { name: name.length > 0 ? name : null, email: contact.email };
        },
        cellRenderer: (params: { data: UserStudyPublic; value: StudyContactValue }) => (
          <StudyContactCell id={params.data.id} value={params.value} />
        ),
      },
      {
        field: 'permissions',
        headerName: tUsers('study_col_permissions'),
        valueFormatter: (params: ValueFormatterParams<UserStudyPublic, string[]>) => (params.value ?? []).join(', '),
      },
      {
        field: 'created_at',
        headerName: tUsers('study_col_created_at'),
        valueFormatter: (params: ValueFormatterParams<UserStudyPublic, string>) => formatDate(params.value),
      },
      { field: 'created_by_full_name', headerName: tUsers('study_col_created_by') },
    ],
    [tUsers, formatDate],
  );

  const orgNameMap = useMemo(() => new Map(orgs.map(o => [o.id, o.name])), [orgs]);

  if (isPending) return <FullLoading label={tUsers('detail_loading')} />;
  // Distinguish "the fetch failed" from "no user has this ID" -- both left `user` falsy before,
  // which misreported a transient list-fetch failure as a definitive "doesn't exist". Gated on
  // `!user` so a failed *background* refetch keeps the still-valid cached user on screen.
  if (isUserError && !user) return <Text>{tUsers('detail_load_error')}</Text>;
  if (!user) return <Text>{tUsers('detail_not_found')}</Text>;

  const primaryOrgId = inferPrimaryOrgId(user);

  const orgMemberships = Object.entries(user.allowed_roles ?? {}).map(([oId, roles]) => ({
    orgId: oId,
    roles,
    isPrimary: oId === primaryOrgId,
  }));

  return (
    <Stack fullHeight fullWidth orientation="vertical">
      <UserActionRow user={user} />

      <Stack className={clsx(styles.content, globalStyles.scrollbars)} gap={4} orientation="vertical">
        <UserHeaderCard user={user} />

        {/* Organization Memberships */}
        <Stack className={styles.section} gap={2} orientation="vertical">
          <Text as="h3">{tUsers('org_memberships')}</Text>
          {orgMemberships.map(({ orgId: oId, roles, isPrimary }) => (
            <Stack alignItems="center" gap={4} key={oId} orientation="horizontal">
              {orgNameMap.has(oId) ? (
                <Link params={{ orgId: oId }} to="/organizations/$orgId">
                  <Text as="span">{orgNameMap.get(oId)}</Text>
                </Link>
              ) : (
                <Text as="span">{oId}</Text>
              )}
              {roles.map(role => (
                <Badge key={role} size="xs" variant="default">
                  {role}
                </Badge>
              ))}
              {isPrimary && <Text as="span">{tUsers('primary_role_indicator')}</Text>}
            </Stack>
          ))}
        </Stack>

        {/* Studies the user has access to */}
        <Stack className={styles.section} gap={2} orientation="vertical">
          {/* Same gate as the user error above: keep already-loaded rows on a refetch failure. */}
          {isStudiesError && studies.length === 0 ? (
            <Text>{tUsers('study_load_error')}</Text>
          ) : (
            <>
              <Text as="h3">{tUsers('study_count', { count: studies.length })}</Text>
              <Table<UserStudyPublic>
                columnDefs={studyColDefs}
                domLayout="autoHeight"
                loading={isStudiesPending}
                rowData={studies}
                tableId="user-studies-table"
              />
            </>
          )}
        </Stack>
      </Stack>
    </Stack>
  );
};

export default UserDetails;
